// store/app.ts
import { axios } from '@/plugins/http-axios';
import { IAddressValue, TSection } from '@ifr/form-questionnaire';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { App, IAction, Okato, Okved } from '@/types/appTypes';
import {
  IUploadedDoc,
  IDocumentType,
  TClientDoc,
  ISubsection,
  IBankSignatory,
  IParticipantSignatory,
  ISignedDocType,
} from '@/types/docTypes';
import { isAppManual as isAppManualUtil } from '@/plugins/utils';
import { getDocumentsForSigning, isSigningBank, isSigningCounterparties } from '@/utils';
import { validatingDocWithRole } from '@/utils/documents';
import { useMainStore } from './mainStore';
import { useSuccessStore } from './success';
import { useEnvStore } from './env';
import { useUserStore } from './user';

export const useAppStore = defineStore('app', () => {
  // State
  const app = ref<App>(new App());
  const clientDocs = ref<TClientDoc[]>([]);
  const fieldDocs = ref<IUploadedDoc[]>([]);
  const fieldDocumentTypes0 = ref<IDocumentType[]>([]);
  const quest = ref<TSection[]>([]);
  const signedDocTypes = ref<ISignedDocType[]>([]);
  const bankSignatories = ref<IBankSignatory[]>([]);
  const participantSignatories = ref<IParticipantSignatory[]>([]);
  const documentSections = ref<ISubsection[]>([]);

  // Геттеры
  const listSignedDocTypes = computed(() => signedDocTypes.value);
  const listBankSignatory = computed(() => bankSignatories.value);
  const listParticipantSignatory = computed(() => participantSignatories.value);

  const account = computed<IAction | undefined>(() => app.value.actions.find((par) => par.type === 'OPEN_ACCOUNT'));

  const accountWithoutIntegration = computed<IAction | undefined>(() =>
    app.value.actions.find((par) => par.type === 'OPEN_ACCOUNT_WITHOUT_INTEGRATION'),
  );

  const application = computed(() => app.value.application);

  const appRegAddress = computed(
    (): IAddressValue => ({
      text: app.value.application.regaddress,
      value: app.value.application.regAddressObject,
    }),
  );

  const appId = computed(() => app.value.application.id);

  const clientPhone = computed(
    () => app.value.application?.client?.phone?.replace(/(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/, '+$1 ($2) $3 $4 $5') || '',
  );

  const clientEmail = computed(() => app.value.application?.client?.email || '');
  const alterEmail = computed((): string => {
    return application.value.additionalNotificationInfo?.emails.join(', ') || '';
  });

  const documentsForSigning = computed(() => getDocumentsForSigning(clientDocs.value));

  const fieldDocumentTypes = computed(() => fieldDocumentTypes0.value);

  const containsAction = computed(
    () =>
      (action: string): boolean =>
        !!app.value.actions.find((item) => item.type === action),
  );

  const isIndividual = computed((): boolean => app.value.application.innEnum === 'INDIVIDUAL_ENTREPRENEURS');

  const isLegalEntity = computed((): boolean => app.value.application.innEnum === 'LEGAL_ENTITY');

  const isPhysicalEntity = computed((): boolean => app.value.application.innEnum === 'PHYSICAL_ENTITY');

  const appType = computed((): string => {
    if (isLegalEntity.value) {
      return 'LEGAL_ENTITY';
    }
    if (isIndividual.value) {
      return 'INDIVIDUAL';
    }
    if (isPhysicalEntity.value) {
      return 'PHYSICAL_ENTITY';
    }
    return '';
  });

  const isAppManual = computed((): boolean => isAppManualUtil(application.value.auto));

  const isStartStatus = computed((): boolean => app.value.application.actualStatus.info === 'START');

  const isOkStop = computed((): boolean => app.value.application.actualStatus.info === 'OK_STOP');

  const isFailStop = computed((): boolean => app.value.application.actualStatus.info === 'FAIL_STOP');
  const isDocUploadBan = computed((): boolean => app.value.application.actualStatus.docUploadBan);

  const getDocumentSubsectionList = computed(() => documentSections.value);

  const isRenamingApplicationFiles = computed((): boolean => {
    const envStore = useEnvStore();
    const userStore = useUserStore();
    return envStore.isOriginFilenameEnable && userStore.hasRenamingApplicationFiles;
  });

  // Actions
  function setApp(data: App) {
    app.value = data;
    app.value.application.okato = new Okato(data.application.okato);
    app.value.application.okved = new Okved(data.application.okved);
  }

  function setClientDocs(data: TClientDoc[]) {
    getDocumentsForSigning(data).forEach((item) => {
      item.docs.forEach((doc) => {
        if (isSigningCounterparties(item)) {
          doc.isParticipantSigned = doc.doc.participantSignatory.isSigned === true;
        } else {
          doc.isParticipantSigned = true;
        }

        if (isSigningBank(item)) {
          doc.isBankSigned = doc.doc.bankSignatory.isSigned === true;
        } else {
          doc.isBankSigned = true;
        }

        doc.isAvailableForSignature = true;
        doc.isSignFinished = doc.isParticipantSigned && doc.isBankSigned;
        doc.isDisabledButton =
          doc.doc.participantSignatory.isSigned === false || doc.doc.bankSignatory.isSigned === false;
      });
    });

    clientDocs.value = data;
  }

  function setFieldDocs(data: IUploadedDoc[]) {
    fieldDocs.value = data;
  }

  function setFieldDocumentTypes(data: IDocumentType[]) {
    fieldDocumentTypes0.value = data;
  }

  function setQuestionnaire(data: TSection[]) {
    quest.value = data;
  }

  function setSignedDocTypes(data: ISignedDocType[] = []) {
    signedDocTypes.value = data;
  }

  function setBankSignatories(data: IBankSignatory[] = []) {
    bankSignatories.value = data;
  }

  function setParticipantSignatories(data: IParticipantSignatory[] = []) {
    participantSignatories.value = data;
  }

  function setDocumentSections(payload: ISubsection[]) {
    documentSections.value = payload.filter((item) => item.active) || [];
  }

  async function getParticipantSignatories(appIdParam?: string): Promise<IParticipantSignatory[]> {
    const mainStore = useMainStore();
    const targetAppId = appIdParam || appId.value;

    if (listParticipantSignatory.value.length) {
      return listParticipantSignatory.value;
    }

    const url = mainStore.getUri.participantSignatories(targetAppId);
    try {
      const resp = await axios.get<IParticipantSignatory[]>(url);
      setParticipantSignatories(resp.data);
      return listParticipantSignatory.value;
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function getSignedDocTypes(appIdParam?: string): Promise<ISignedDocType[]> {
    const mainStore = useMainStore();
    const userStore = useUserStore();
    const targetAppId = appIdParam || appId.value;

    if (listSignedDocTypes.value.length) {
      return listSignedDocTypes.value;
    }

    const url = mainStore.getUri.signedDocTypes(targetAppId);
    try {
      const resp = await axios.get<ISignedDocType[]>(url);
      setSignedDocTypes(validatingDocWithRole(resp.data, userStore.role.id));
      return listSignedDocTypes.value;
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function getBankSignatories(): Promise<IBankSignatory[]> {
    const mainStore = useMainStore();

    if (listBankSignatory.value.length) {
      return listBankSignatory.value;
    }

    if (!app.value.application.bankoffice?.id) {
      return [];
    }

    const url = mainStore.getUri.bankSignatoriesByOfficeId(app.value.application.bankoffice.id);
    try {
      const resp = await axios.get<IBankSignatory[]>(url);
      setBankSignatories(resp.data);
      return listBankSignatory.value;
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function getQuestionnaire(id?: string): Promise<TSection[]> {
    const mainStore = useMainStore();
    const successStore = useSuccessStore();

    id = id || app.value.application.id || '';
    try {
      const resp = await axios.get<TSection[]>(mainStore.getUri.questionnaire(id), { skipErrorMsgByStatuses: [409] });
      setQuestionnaire(resp.data);
      return quest.value;
    } catch (e: any) {
      if (e.response && e.response.status === 409) {
        successStore.addSuccess({ message: 'Невозможно сохранить анкету!', color: 'error' });
      }
      return Promise.reject(e);
    }
  }

  async function getApp(id?: string): Promise<void> {
    const mainStore = useMainStore();
    const targetId = id || app.value.application.id;

    try {
      const resp = await axios.get<App>(mainStore.getUri.appIdNew(targetId));
      setApp(resp.data);
      setSignedDocTypes();
      setBankSignatories();
      setParticipantSignatories();
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function getClientDocs(id?: string) {
    const mainStore = useMainStore();
    const userStore = useUserStore();
    const targetId = id || app.value.application.id;

    try {
      const resp = await axios.get<TClientDoc[]>(mainStore.getUri.clientDocs(targetId));
      setClientDocs(validatingDocWithRole(resp.data, userStore.role.id));
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function getFieldDocs(id?: string) {
    const mainStore = useMainStore();
    const userStore = useUserStore();
    const targetId = id || app.value.application.id;

    try {
      const resp = await axios.get<IUploadedDoc[]>(mainStore.getUri.fieldDocs(targetId));
      setFieldDocs(validatingDocWithRole(resp.data, userStore.role.id));
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function getFieldDocumentTypes(id?: string) {
    const mainStore = useMainStore();
    const userStore = useUserStore();
    const targetId = id || app.value.application.id;

    try {
      const resp = await axios.get<IDocumentType[]>(mainStore.getUri.fieldDocumentTypes(targetId));
      setFieldDocumentTypes(validatingDocWithRole(resp.data, userStore.role.id));
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  async function fetchDocumentSectionList() {
    const mainStore = useMainStore();

    const response = await axios.get<ISubsection[]>(mainStore.getUri.secionList);
    setDocumentSections(response.data);
    return response;
  }

  // Возвращаем всё, что должно быть доступно из store
  return {
    // state
    app,
    clientDocs,
    fieldDocs,
    fieldDocumentTypes0,
    quest,
    signedDocTypes,
    bankSignatories,
    participantSignatories,
    documentSections,

    // getters
    listSignedDocTypes,
    listBankSignatory,
    listParticipantSignatory,
    account,
    accountWithoutIntegration,
    application,
    appRegAddress,
    appId,
    clientPhone,
    clientEmail,
    alterEmail,
    documentsForSigning,
    fieldDocumentTypes,
    containsAction,
    isIndividual,
    isLegalEntity,
    isPhysicalEntity,
    appType,
    isAppManual,
    isStartStatus,
    isOkStop,
    isFailStop,
    getDocumentSubsectionList,
    isRenamingApplicationFiles,
    isDocUploadBan,

    // actions
    setApp,
    setClientDocs,
    setFieldDocs,
    setFieldDocumentTypes,
    setQuestionnaire,
    setSignedDocTypes,
    setBankSignatories,
    setParticipantSignatories,
    setDocumentSections,
    getParticipantSignatories,
    getSignedDocTypes,
    getBankSignatories,
    getQuestionnaire,
    getApp,
    getClientDocs,
    getFieldDocs,
    getFieldDocumentTypes,
    fetchDocumentSectionList,
  };
});

export default useAppStore;
